import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injector,
  Renderer2,
} from '@angular/core';

import type * as EchartsType from 'echarts/types/dist/shared';
import { TooltipOption, TooltipPositionCallback } from 'echarts/types/dist/shared';

import { ReportsEChartsOption } from '@core/reports-types';

let memoizedTooltip: null | {
  memoizeResult: string;
  component: ComponentRef<any>;
  el: HTMLElement;
};

// Tooltip can return either string or HTMLElement
// HTMLElement is used for complex tooltips and we use it to display any angular component
// While moving the mouse over the chart, formatter function is called all the time
// That is why tooltip is memoized to avoid creating new components
export const addTooltip =
  (config: {
    injector: Injector;
    componentClass: any;
    memoizeFn: (params: EchartsType.TopLevelFormatterParams) => string;
    componentInputs?: (params: EchartsType.TopLevelFormatterParams) => Record<string, any>;
    containerEl?: HTMLElement | null;
  }) =>
  <T extends ReportsEChartsOption>(
    opts: T,
  ): T & {
    tooltip: EchartsType.TooltipOption;
  } => ({
    ...opts,
    tooltip: {
      ...opts.tooltip,
      padding: 0,
      shadowColor: 'none',
      shadowOffsetY: 0,
      shadowOffsetX: 0,
      // root element of the app
      appendTo: (() => {
        if (config.containerEl) {
          return config.containerEl;
        }
        const appRef = config.injector.get(ApplicationRef);
        const rootComponent = appRef.components[0];
        if (rootComponent) {
          return rootComponent.location.nativeElement;
        }
        return document.body;
      })(),
      backgroundColor: 'rgba(0, 0, 0, 0)',
      borderWidth: 0,
      borderRadius: 0,
      confine: false,
      // position tooltip above our dialogs
      extraCssText: 'z-index: 2147483000',
      formatter: (params: EchartsType.TopLevelFormatterParams) => {
        const memoizeResult = config.memoizeFn(params);
        if (memoizedTooltip) {
          {
            const { memoizeResult: prevMemoizedResult, component, el } = memoizedTooltip;
            if (memoizeResult === prevMemoizedResult) {
              return el;
            }
            component.destroy();
            el?.remove();
          }
        }
        const div = config.injector.get(Renderer2).createElement('div');
        const component = createComponent(config.componentClass, {
          hostElement: div,
          environmentInjector: config.injector.get(EnvironmentInjector),
        });

        if (config.componentInputs) {
          Object.entries(config.componentInputs(params)).forEach(([key, value]) => {
            component.setInput(key, value);
          });
        }
        component.changeDetectorRef.detectChanges();
        const appRef = config.injector.get(ApplicationRef);
        appRef.attachView(component.hostView);

        memoizedTooltip = { memoizeResult, component, el: div };

        return div;
      },
    } satisfies EchartsType.TooltipOption,
  });

// display tooltip above the cursor.
// if it overflows the screen, move it to the left or right
export const getDefaultTooltipPosition =
  (containerEl: HTMLElement): TooltipPositionCallback =>
  (point, params, dom, rect, size) => {
    // mouse position is from the container, not the document
    const xMousePosition = point[0] + containerEl.getBoundingClientRect().left;
    const width = size.contentSize[0];
    const height = size.contentSize[1];
    const parentSize = (<HTMLDivElement>dom)?.parentElement!.offsetWidth;

    if (xMousePosition - width / 2 <= 0) {
      return [point[0] + 10, point[1] - height / 2];
    }
    if (xMousePosition + width / 2 >= parentSize) {
      return [point[0] - width - 10, point[1] - height / 2];
    }

    return [point[0] - width / 2, point[1] - height - 10];
  };

export const addTooltipPosition =
  (containerEl: HTMLElement) =>
  <T extends { tooltip: TooltipOption }>(opts: T): T =>
    ({
      ...opts,
      tooltip: {
        ...opts.tooltip,
        position: addTooltipPosition(containerEl),
      },
    }) satisfies ReportsEChartsOption;

export const enableTooltipTriggerOnAxis = <T extends { tooltip: TooltipOption }>(opts: T): T => ({
  ...opts,
  tooltip: {
    ...opts.tooltip,
    trigger: <const>'axis',
    axisPointer: {
      type: <const>'line',
    },
  },
});
